import * as Factory from 'factory.ts'
import faker from 'faker'

import { CurrencyApiModelStub } from '@webapp/core/data-access/api-models/stubs/CurrencyApiModel.stub'
import { type CountryCode } from '@webapp/core/entities/external/Country'
import { PaymentMethodTypes } from '@webapp/platform/banking/entities/enums/PaymentMethodTypeEnum'
import { type PaymentMethodApiModel } from '../PaymentMethodApiModel'

export const PaymentMethodApiModelStub =
  Factory.Sync.makeFactory<PaymentMethodApiModel>({
    id: Factory.each(() => faker.random.number()),
    currency: CurrencyApiModelStub.build(),
    data: Factory.each(() => ({
      description: faker.finance.accountName(),
      payable_to: faker.lorem.word(),
      account_number: faker.finance.account(),
      routing_number: faker.finance.account(),
      company_name: faker.company.companyName(),
    })),
    name: Factory.each(() => faker.lorem.words(2)),
    type: PaymentMethodTypes.OTHER,
    vendor: Factory.each(() => faker.random.number()),
    payment_rail: 'ACH',
    bank_account_country_code: 'US',
    transaction_fee: null,
    flight_time: null,
  })
    .withDerivation1(['type'], 'data', (type) => {
      switch (type) {
        case PaymentMethodTypes.CHECK:
          return {
            payable_to: faker.company.companyName(),
          }
        case PaymentMethodTypes.ACH:
          return {
            routing_number: faker.finance.account(),
            company_name: faker.company.companyName(),
            account_number: faker.finance.account(),
          }
        case PaymentMethodTypes.EFT:
          return {
            bank_account_holder_name: faker.name.firstName(),
            bank_name: faker.finance.accountName(),
            institution_number: '***',
            transit_number: '***45',
            account_number: '****5678',
            payee_name: faker.company.companyName(),
            street_address: faker.address.streetAddress(),
            city: faker.address.city(),
            province: 'CA-AB',
            postal_code: 'A1A 1A1',
          }
        case PaymentMethodTypes.PROCURIFY_ACH:
        case PaymentMethodTypes.PROCURIFY_WIRE:
          return {
            bank_account_holder_name: faker.name.firstName(),
            bank_name: faker.finance.accountName(),
            account_number: '****5678',
            routing_number: '****5678',
            payee_name: faker.company.companyName(),
            street_address: faker.address.streetAddress(),
            city: faker.address.city(),
            state: 'WA',
            zip_code: '12345',
          }
        case PaymentMethodTypes.PROCURIFY_CHECK:
          return {
            payee_name: faker.company.companyName(),
            street_address: faker.address.streetAddress(),
            city: faker.address.city(),
            state: 'WA',
            zip_code: '12345',
          }
        case PaymentMethodTypes.AIRWALLEX:
          return {
            beneficiary: {
              company_name: faker.company.companyName(),
              address: {
                city: faker.address.city(),
                country_code: faker.address.countryCode() as CountryCode,
                postcode: faker.address.zipCode(),
                state: faker.address.stateAbbr(),
                street_address: faker.address.streetAddress(),
              },
              bank_details: {
                account_currency: 'USD',
                account_number: faker.finance.account(),
                account_name: faker.name.firstName(),
                account_routing_type1: 'SWIFT',
                account_routing_value1: 'ABCDEF12',
                bank_account_category: 'PERSONAL',
                bank_country_code: faker.address.countryCode(),
                local_clearing_system: 'ABA',
                iban: 'GB33BUKB20201555555555',
                swift_code: 'ABCDEF12',
              },
              entity_type: 'INDIVIDUAL',
            },
          }
        case PaymentMethodTypes.OTHER:
        default:
          return {
            description: faker.finance.accountName(),
          }
      }
    })
    .withDerivation1(['type'], 'currency', (type) => {
      switch (type) {
        case PaymentMethodTypes.ACH:
          return CurrencyApiModelStub.build({ name: 'USD' })
        case PaymentMethodTypes.EFT:
          return CurrencyApiModelStub.build({ name: 'CAD' })
        default:
          return CurrencyApiModelStub.build()
      }
    })
